import { getDataSource } from '@/lib/data-source';
import type { RequestConfig } from '@/lib/data-source/types';

export interface SignupRedirectSettings {
  enabled: boolean;
  url: string | null;
  message?: string | null;
  signup: {
    enabled: boolean;
    url: string | null;
  };
  verification: {
    enabled: boolean;
    url: string | null;
  };
}

export interface SignupRedirectUpdateInput {
  enabled: boolean;
  url?: string | null;
  verificationEnabled: boolean;
  verificationUrl?: string | null;
}

interface SignupRedirectApiPayload {
  data?: {
    settings?: {
      enabled?: boolean;
      url?: string | null;
      signup?: {
        enabled?: boolean;
        url?: string | null;
      };
      verification?: {
        enabled?: boolean;
        url?: string | null;
      };
      signup_redirect_enabled?: boolean;
      signup_redirect_url?: string | null;
      verification_redirect_enabled?: boolean;
      verification_redirect_url?: string | null;
    };
  };
  settings?: {
    enabled?: boolean;
    url?: string | null;
    signup?: {
      enabled?: boolean;
      url?: string | null;
    };
    verification?: {
      enabled?: boolean;
      url?: string | null;
    };
    signup_redirect_enabled?: boolean;
    signup_redirect_url?: string | null;
    verification_redirect_enabled?: boolean;
    verification_redirect_url?: string | null;
  };
  meta?: Record<string, unknown>;
  [key: string]: unknown;
}

class SignupRedirectSettingsService {
  private dataSource = getDataSource();

  private withProviderScope(config: RequestConfig = {}): RequestConfig {
    return {
      ...config,
      headers: {
        ...(config.headers ?? {}),
        'X-Florentin-Auth-Scope': 'provider',
      },
    };
  }

  private normalize(payload: unknown, meta?: Record<string, unknown>): SignupRedirectSettings {
    if (!payload || typeof payload !== 'object') {
      return {
        enabled: false,
        url: null,
        message: meta?.message as string | null | undefined,
        signup: { enabled: false, url: null },
        verification: { enabled: false, url: null },
      };
    }

    const record = payload as Record<string, unknown>;
    const signupSettings = (record.signup as Record<string, unknown> | undefined) ?? {};
    const verificationSettings = (record.verification as Record<string, unknown> | undefined) ?? {};

    const enabledRaw =
      record.enabled ??
      signupSettings.enabled ??
      record.signup_redirect_enabled ??
      signupSettings.signup_redirect_enabled;
    const urlRaw =
      record.url ??
      signupSettings.url ??
      record.signup_redirect_url ??
      signupSettings.signup_redirect_url;

    const verificationEnabledRaw =
      verificationSettings.enabled ?? record.verification_redirect_enabled ?? verificationSettings.verification_redirect_enabled;
    const verificationUrlRaw =
      verificationSettings.url ?? record.verification_redirect_url ?? verificationSettings.verification_redirect_url;

    return {
      enabled: typeof enabledRaw === 'boolean' ? enabledRaw : false,
      url: typeof urlRaw === 'string' ? urlRaw : null,
      message: (meta?.message as string | undefined) ?? null,
      signup: {
        enabled: typeof enabledRaw === 'boolean' ? enabledRaw : false,
        url: typeof urlRaw === 'string' ? urlRaw : null,
      },
      verification: {
        enabled: typeof verificationEnabledRaw === 'boolean' ? verificationEnabledRaw : false,
        url: typeof verificationUrlRaw === 'string' ? verificationUrlRaw : null,
      },
    };
  }

  private extractSettings(payload: unknown): Record<string, unknown> {
    if (!payload || typeof payload !== 'object') {
      return {};
    }

    const record = payload as Record<string, unknown>;
    if (record.settings) {
      return this.extractSettings(record.settings);
    }
    if (record.data) {
      return this.extractSettings(record.data);
    }
    return record;
  }

  async getSettings(): Promise<SignupRedirectSettings> {
    const response = await this.dataSource.get<SignupRedirectApiPayload>(
      '/api/v1/settings/signup-redirect',
      this.withProviderScope(),
    );

    const settings = this.extractSettings(response.data);
    return this.normalize(settings, response.meta);
  }

  async updateSettings(input: SignupRedirectUpdateInput): Promise<SignupRedirectSettings> {
    const payload: Record<string, unknown> = {
      signup_redirect_enabled: input.enabled,
      verification_redirect_enabled: input.verificationEnabled,
    };

    if (input.enabled) {
      payload.signup_redirect_url = input.url?.trim() ?? '';
    } else {
      payload.signup_redirect_url = input.url?.trim() ?? null;
    }

    if (input.verificationEnabled) {
      payload.verification_redirect_url = input.verificationUrl?.trim() ?? '';
    } else {
      payload.verification_redirect_url = input.verificationUrl?.trim() ?? null;
    }

    const response = await this.dataSource.put<SignupRedirectApiPayload>(
      '/api/v1/settings/signup-redirect',
      payload,
      this.withProviderScope(),
    );

    const settings = this.extractSettings(response.data);
    return this.normalize(settings, response.meta);
  }
}

export const signupRedirectSettingsService = new SignupRedirectSettingsService();
